import { AbstractControl, FormGroup, ValidationErrors } from '@angular/forms';
import { GetDocumentTypeDto } from 'src/app/modules/doc-types/models/doc-types.model';
import {
  getDocumentPlaybookComplianceScores,
  getDocumentReviewMetric,
  GetDocumentDto,
} from '../../models/documents.model';
import { LanguageDto } from 'src/app/shared/services/languages-api.service';
import { PlaybookDto } from 'src/app/modules/playbooks/models/playbooks.model';
import {
  buildPlaybookPreviewName,
  PlaybookNameContext,
} from 'src/app/modules/playbooks/playbooks.helper';
import { PositionDto } from 'src/app/shared/services/positions-api.service';
import { ReportContent } from 'src/app/shared/services/reporting-api.service';
import {
  DocumentProcessingClause,
  FlagType,
} from '../../services/document-processing-api.service';
import {
  DocumentWithPlaybookSelection,
  PlaybookComplianceReviewItem,
  PlaybookComplianceReviewSummary,
  PlaybookOption,
} from './document-details-drawer.models';

export function buildDocumentDetailsPlaybookOptions(
  playbooks: PlaybookDto[],
  context: PlaybookNameContext,
): PlaybookOption[] {
  return [...playbooks]
    .map((playbook) => {
      const documentTypeName =
        context.docTypeItems.find((docType) => docType.id === playbook.documentTypeId)?.name ||
        context.selectPlaceholder;
      const positionName =
        context.positionItems.find((position) => position.id === playbook.positionId)?.name ||
        context.selectPlaceholder;
      const languageName =
        context.contentLanguages.find((language) => language.id === playbook.languageId)
          ?.displayName || context.selectPlaceholder;

      return {
        ...playbook,
        displayName: buildPlaybookPreviewName(playbook, context),
        documentTypeName,
        positionName,
        languageName,
      };
    })
    .sort((left, right) => left.displayName.localeCompare(right.displayName));
}

export function buildPlaybookNameContext(
  docTypeItems: GetDocumentTypeDto[],
  positionItems: PositionDto[],
  contentLanguages: LanguageDto[],
  selectPlaceholder: string,
  nonePlaceholder: string,
): PlaybookNameContext {
  return {
    docTypeItems: docTypeItems || [],
    positionItems: positionItems || [],
    contentLanguages: contentLanguages || [],
    selectPlaceholder,
    nonePlaceholder,
  };
}

export function getSuggestedReportPlaybooks(
  playbookItems: PlaybookOption[],
  documentTypeId: number | undefined,
  languageId: number | undefined,
  positionId: number | undefined,
): PlaybookOption[] {
  if (!documentTypeId || !languageId || !positionId) {
    return [];
  }

  return playbookItems.filter(
    (playbook) =>
      playbook.documentTypeId === documentTypeId &&
      playbook.languageId === languageId &&
      playbook.positionId === positionId,
  );
}

export function resolveInitialReportPlaybookId(
  document: GetDocumentDto,
  playbookItems: PlaybookOption[],
): string | null {
  const documentWithPlaybookSelection = document as DocumentWithPlaybookSelection;
  const playbookId =
    typeof documentWithPlaybookSelection.assignedPlaybookId === 'string'
      ? documentWithPlaybookSelection.assignedPlaybookId
      : typeof documentWithPlaybookSelection.playbookId === 'string'
        ? documentWithPlaybookSelection.playbookId
        : typeof documentWithPlaybookSelection.playbookUniqueId === 'string'
          ? documentWithPlaybookSelection.playbookUniqueId
          : null;

  return isKnownPlaybookId(playbookId, playbookItems) ? playbookId : null;
}

export function isKnownPlaybookId(
  playbookId: string | null | undefined,
  playbookItems: PlaybookOption[],
): boolean {
  return !!playbookId && playbookItems.some((playbook) => playbook.uniqueId === playbookId);
}

export function buildPlaybookComplianceReview(
  document: GetDocumentDto,
  stateReviewResponseData: GetDocumentDto['playbookComplianceScores'] | undefined,
): PlaybookComplianceReviewSummary | null {
  const reviewResponseData =
    stateReviewResponseData || document.playbookComplianceScores || document.reviewResponseData;
  const reviewMetric = getDocumentReviewMetric(reviewResponseData);
  const clauses = getDocumentPlaybookComplianceScores(reviewResponseData).flatMap(
    (score) => score.clauses || [],
  );

  if (!reviewMetric || !clauses.length) {
    return null;
  }

  return {
    score: reviewMetric.score || 0,
    greenCount: reviewMetric.matched,
    orangeCount: reviewMetric.warnings,
    redCount: reviewMetric.failed,
    items: clauses.map((clause) => mapPlaybookComplianceReviewItem(clause)),
  };
}

export function getFlagClass(flagType: FlagType): string {
  switch (flagType) {
    case FlagType.MostDesired:
      return 'document-details-drawer__flag--green';
    case FlagType.Fallback:
      return 'document-details-drawer__flag--orange';
    default:
      return 'document-details-drawer__flag--red';
  }
}

export function getComplianceStatusClass(flagType: FlagType): string {
  switch (flagType) {
    case FlagType.MostDesired:
      return 'document-details-drawer__compliance-status--green';
    case FlagType.Fallback:
      return 'document-details-drawer__compliance-status--orange';
    default:
      return 'document-details-drawer__compliance-status--red';
  }
}

export function getComplianceStatusLabelKey(flagType: FlagType): string {
  switch (flagType) {
    case FlagType.MostDesired:
      return 'labels.compliant';
    case FlagType.Fallback:
      return 'labels.partiallyCompliant';
    default:
      return 'labels.nonCompliant';
  }
}

export function getReportContents(reportForm: FormGroup | undefined): ReportContent[] {
  const contents: ReportContent[] = [];
  const includePlaybookAnalysis = reportForm?.get('reportContentPlaybookAnalysis')?.value === true;

  if (reportForm?.get('reportContentSummary')?.value) {
    contents.push(ReportContent.Summary);
  }

  if (includePlaybookAnalysis && reportForm?.get('reportContentPlaybookCompliance')?.value) {
    contents.push(ReportContent.PlaybookCompliance);
  }

  if (includePlaybookAnalysis && reportForm?.get('reportContentKeyQuestions')?.value) {
    contents.push(ReportContent.KeyQuestions);
  }

  if (reportForm?.get('reportContentIntegrityCheck')?.value) {
    contents.push(ReportContent.IntegrityCheck);
  }

  if (reportForm?.get('reportContentRegulatoryCompliance')?.value) {
    contents.push(ReportContent.RegulatoryCompliance);
  }

  if (reportForm?.get('reportContentReadabilityAnalysis')?.value) {
    contents.push(ReportContent.ReadabilityAnalysis);
  }

  return contents;
}

export function reportContentValidator(control: AbstractControl): ValidationErrors | null {
  const group = control as FormGroup;
  const keys = [
    'reportContentSummary',
    'reportContentPlaybookCompliance',
    'reportContentKeyQuestions',
    'reportContentIntegrityCheck',
    'reportContentRegulatoryCompliance',
    'reportContentReadabilityAnalysis',
  ];

  const anySelected = keys.some((key) => !!group.get(key)?.value);
  return anySelected ? null : { reportContentRequired: true };
}

export function getNumericValue(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : undefined;
}

export function getSelectedCounterpartyIds(value: unknown): number[] {
  if (Array.isArray(value)) {
    return value.map((item) => getNumericValue(item)).filter((item): item is number => item != null);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => getNumericValue(item.trim()))
      .filter((item): item is number => item != null);
  }

  const numericValue = getNumericValue(value);
  return numericValue != null ? [numericValue] : [];
}

export function hasPersistedDocumentChanged(
  lastSavedDocument: GetDocumentDto | null,
  document: GetDocumentDto,
  playbookItems: PlaybookOption[],
): boolean {
  if (!lastSavedDocument) {
    return true;
  }

  return (
    lastSavedDocument.uniqueId !== document.uniqueId ||
    lastSavedDocument.name !== document.name ||
    lastSavedDocument.type !== document.type ||
    lastSavedDocument.userPositionId !== document.userPositionId ||
    lastSavedDocument.status !== document.status ||
    lastSavedDocument.languageId !== document.languageId ||
    lastSavedDocument.revisionOption !== document.revisionOption ||
    resolveInitialReportPlaybookId(lastSavedDocument, playbookItems) !==
      resolveInitialReportPlaybookId(document, playbookItems) ||
    !areSameNumberArray(lastSavedDocument.parties, document.parties)
  );
}

export function cloneDocument(document: GetDocumentDto): GetDocumentDto {
  return {
    ...document,
    parties: [...document.parties],
  };
}

function mapPlaybookComplianceReviewItem(
  clause: DocumentProcessingClause,
): PlaybookComplianceReviewItem {
  const clauseDetails = clause as DocumentProcessingClause & {
    title?: string;
    name?: string;
    clauseName?: string;
    reviewResponse?: {
      clause?: {
        header?: string;
        title?: string;
        name?: string;
        clauseName?: string;
        explanation?: string;
      };
    };
  };

  return {
    id: clause.id,
    title:
      clause.header ||
      clauseDetails.title ||
      clauseDetails.name ||
      clauseDetails.clauseName ||
      clause.reviewResponse?.clause?.header ||
      clauseDetails.reviewResponse?.clause?.title ||
      clauseDetails.reviewResponse?.clause?.name ||
      clauseDetails.reviewResponse?.clause?.clauseName ||
      '',
    explanation: clause.explanation || clause.reviewResponse?.clause?.explanation || '',
    flagType: clause.flagType,
  };
}

function areSameNumberArray(first: number[] = [], second: number[] = []): boolean {
  return first.length === second.length && first.every((value, index) => value === second[index]);
}